/**
 * Shared tile geometry + materials toolkit.
 *
 * Both mahjong and domino tiles are the same physical thing: a beveled
 * rounded brick lying flat on the table, with a printed face material on
 * top. They differ only in dimensions (1 × 1.32 × 0.30 vs 2 × 1 × 0.26)
 * and what's printed on the face. This module owns the shared geometry,
 * the side-AO-gradient bake, and mesh assembly + dim helper.
 *
 * Each consumer module supplies its own caps/sides/face materials and
 * builds the per-tile face material — the rest is parameterized.
 */

import {
  CanvasTexture,
  ExtrudeGeometry,
  Group,
  Mesh,
  MeshPhysicalMaterial,
  Object3D,
  RepeatWrapping,
  Shape,
  ShapeGeometry,
  SRGBColorSpace,
  type Material,
} from 'three';
import { SHADOW_CASTER_LAYER } from './shadow-pass.js';

/** Top-down footprint + thickness, in world units. */
export interface TileDims {
  /** Size along world X (the "width" of the tile, lying flat). */
  sizeX: number;
  /** Size along world Z (the "depth" of the tile, lying flat). */
  sizeZ: number;
  /** Thickness along world Y. */
  depth: number;
  cornerRadius: number;
  bevel: number;
}

/** A 2D rounded-rect Shape centered on origin. */
export function roundedRectShape(w: number, h: number, r: number): Shape {
  const s = new Shape();
  const x = -w / 2;
  const y = -h / 2;
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r);
  s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h);
  s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r);
  s.quadraticCurveTo(x, y, x + r, y);
  return s;
}

/**
 * Body geometry — a rounded brick with bevel. The geometry sits centered
 * at the origin with the printed face up (+y). MaterialIndex 0 = caps
 * (top + bottom), index 1 = side walls.
 */
export function buildBodyGeometry(dims: TileDims): ExtrudeGeometry {
  const { sizeX, sizeZ, depth, cornerRadius, bevel } = dims;
  const extrudeDepth = depth - 2 * bevel;
  const cap = roundedRectShape(sizeX - 2 * bevel, sizeZ - 2 * bevel, cornerRadius - bevel);
  const g = new ExtrudeGeometry(cap, {
    depth: extrudeDepth,
    bevelEnabled: true,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelOffset: 0,
    bevelSegments: 8,
    curveSegments: 14,
  });
  g.translate(0, 0, -extrudeDepth / 2);
  g.rotateX(-Math.PI / 2);
  g.computeVertexNormals();
  return g;
}

/**
 * Face plate geometry — a flat rounded-rect that sits on top of the body,
 * matching the body's outer silhouette so there's no rim of body color
 * peeking around the face. UVs are normalized to [0,1] over the rect so
 * a face material's texture maps directly.
 */
export function buildFaceGeometry(dims: TileDims): ShapeGeometry {
  const { sizeX, sizeZ, cornerRadius } = dims;
  const shape = roundedRectShape(sizeX, sizeZ, cornerRadius);
  const g = new ShapeGeometry(shape);
  const uv = g.attributes.uv;
  if (uv) {
    for (let i = 0; i < uv.count; i++) {
      uv.setXY(i, (uv.getX(i) + sizeX / 2) / sizeX, (uv.getY(i) + sizeZ / 2) / sizeZ);
    }
    uv.needsUpdate = true;
  }
  return g;
}

/**
 * Bake a vertical AO gradient into a side-wall texture. Bright at the top
 * (UV.y=1, where the bevel catches highlights) and ~50% darker at the
 * bottom (UV.y=0, where the tile meets the table). Reads as ambient
 * occlusion in the gap between adjacent tiles without an AO post-pass —
 * the same trick used by mahjong.wtf's blender breakdown.
 *
 * ExtrudeGeometry's WorldUVGenerator assigns UV.y = 1 - z (in pre-rotation
 * extrusion space). Our `buildBodyGeometry` runs extrusion 0 → +depth
 * then rotates by -π/2 around X, so verts at the original z=0 (now +Y,
 * the top of the tile) get UV.y=1 and verts at z=+depth (now -Y, bottom)
 * get UV.y≈0. Bevel verts share the gradient so the bottom fillet darkens
 * with the rest of the side wall.
 */
export function buildSideGradientTexture(opts?: {
  topColor?: string;
  midColor?: string;
  bottomColor?: string;
}): CanvasTexture {
  const W = 4;
  const H = 256;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0.0, opts?.topColor ?? '#ffffff');
  grad.addColorStop(0.55, opts?.midColor ?? '#d2d2d2');
  grad.addColorStop(1.0, opts?.bottomColor ?? '#7e7e7e');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);
  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.wrapS = tex.wrapT = RepeatWrapping;
  return tex;
}

/** Per-tile dim factor — used so blocked / non-active tiles read darker. */
export interface TileDimMaterials {
  caps: MeshPhysicalMaterial;
  sides: MeshPhysicalMaterial;
  face?: MeshPhysicalMaterial;
  capsBaseColor: { r: number; g: number; b: number };
  sidesBaseColor: { r: number; g: number; b: number };
  faceBaseColor?: { r: number; g: number; b: number };
}

export interface AssembleTileOptions {
  bodyGeom: ExtrudeGeometry;
  faceGeom: ShapeGeometry;
  dims: TileDims;
  /** How far above the body's top to lift the face plate (avoids z-fight). */
  faceLift: number;
  capsMat: MeshPhysicalMaterial;
  sidesMat: MeshPhysicalMaterial;
  faceMat: Material;
  /** Whether the face mesh receives shadows. Defaults to true. */
  faceReceivesShadow?: boolean;
}

/** Assemble a tile mesh group: a body (caps + sides) and a face plate
 *  inset on top. Adds the SHADOW_CASTER_LAYER to all children and stores
 *  the dimmable material refs in `group.userData.materials`. */
export function assembleTileMesh(opts: AssembleTileOptions): Group {
  const { bodyGeom, faceGeom, dims, faceLift, capsMat, sidesMat, faceMat } = opts;
  const group = new Group();

  // ExtrudeGeometry assigns materialIndex 0 to caps, 1 to sides.
  const body = new Mesh(bodyGeom, [capsMat, sidesMat]);
  body.castShadow = true;
  body.receiveShadow = true;
  group.add(body);

  const face = new Mesh(faceGeom, faceMat);
  face.rotation.x = -Math.PI / 2;
  face.position.y = dims.depth / 2 + faceLift;
  face.receiveShadow = opts.faceReceivesShadow ?? true;
  group.add(face);

  const faceMatPhys = (faceMat instanceof MeshPhysicalMaterial) ? faceMat : undefined;
  group.userData.materials = {
    caps: capsMat,
    sides: sidesMat,
    face: faceMatPhys,
    capsBaseColor: capsMat.color.clone(),
    sidesBaseColor: sidesMat.color.clone(),
    faceBaseColor: faceMatPhys?.color.clone(),
  } satisfies TileDimMaterials;

  group.traverse((c) => c.layers.enable(SHADOW_CASTER_LAYER));
  return group;
}

/** Scale a tile's stored body+face material colors by `factor`
 *  (1 = full bright, 0.6 = dimmed). Mahjong uses this for the
 *  blocked-tile darken effect; dominoes uses it the same way. */
export function setTileDim(group: Object3D, factor: number): void {
  const m = group.userData.materials as TileDimMaterials | undefined;
  if (!m) return;
  m.caps.color.setRGB(
    m.capsBaseColor.r * factor,
    m.capsBaseColor.g * factor,
    m.capsBaseColor.b * factor,
  );
  m.sides.color.setRGB(
    m.sidesBaseColor.r * factor,
    m.sidesBaseColor.g * factor,
    m.sidesBaseColor.b * factor,
  );
  if (m.face && m.faceBaseColor) {
    m.face.color.setRGB(
      m.faceBaseColor.r * factor,
      m.faceBaseColor.g * factor,
      m.faceBaseColor.b * factor,
    );
  }
}
